import clsx from 'clsx'
import {KTSVG} from '../../../helpers'
import {ThemeModeType, useThemeMode, systemMode} from './ThemeModeProvider'
import {useThemeCustomizer} from '../../../../app/contexts/ThemeCustomizerContext'
import {setActiveThemeId} from '../../../../app/services/themeCustomizerService'

/* eslint-disable jsx-a11y/anchor-is-valid */
type Props = {
  toggleBtnClass?: string
  toggleBtnIconClass?: string
  menuPlacement?: string
  menuTrigger?: string
}

const ThemeModeSwitcher = ({
  toggleBtnClass = '',
  toggleBtnIconClass = 'svg-icon-2',
  menuPlacement = 'bottom-end',
  menuTrigger = "{default: 'click', lg: 'hover'}",
}: Props) => {
  const {mode, menuMode, updateMode, updateMenuMode} = useThemeMode()
  const {themes, activeThemeId} = useThemeCustomizer()
  const calculatedMode = mode === 'system' ? systemMode : mode
  const switchMode = (_mode: ThemeModeType) => {
    updateMenuMode(_mode)
    updateMode(_mode)
  }

  return (
    <>
      {/* begin::Menu toggle */}
      <a
        href='#'
        className={clsx('btn btn-icon ', toggleBtnClass)}
        data-kt-menu-trigger={menuTrigger}
        data-kt-menu-attach='parent'
        data-kt-menu-placement={menuPlacement}
      >
        {calculatedMode === 'dark' && (
          <KTSVG
            path='/media/icons/duotune/general/gen061.svg'
            className={clsx('theme-light-hide', toggleBtnIconClass)}
          />
        )}

        {calculatedMode === 'light' && (
          <KTSVG
            path='/media/icons/duotune/general/gen060.svg'
            className={clsx('theme-dark-hide', toggleBtnIconClass)}
          />
        )}

        {/* Night theme removed */}
      </a>
      {/* begin::Menu toggle */}

      {/* begin::Menu */}
      <div
        className='menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-title-gray-700 menu-icon-muted menu-active-bg menu-state-primary fw-semibold py-4 fs-base w-175px'
        data-kt-menu='true'
      >
        {/* begin::Menu item */}
        <div className='menu-item px-3 my-0'>
          <a
            href='#'
            className={clsx('menu-link px-3 py-2', {active: menuMode === 'light'})}
            onClick={() => switchMode('light')}
          >
            <span className='menu-icon' data-kt-element='icon'>
              <KTSVG path='/media/icons/duotune/general/gen060.svg' className='svg-icon-3' />
            </span>
            <span className='menu-title'>Light</span>
          </a>
        </div>
        {/* end::Menu item */}

        {/* begin::Menu item */}
        <div className='menu-item px-3 my-0'>
          <a
            href='#'
            className={clsx('menu-link px-3 py-2', {active: menuMode === 'dark'})}
            onClick={() => switchMode('dark')}
          >
            <span className='menu-icon' data-kt-element='icon'>
              <KTSVG path='/media/icons/duotune/general/gen061.svg' className='svg-icon-3' />
            </span>
            <span className='menu-title'>Dark</span>
          </a>
        </div>
        {/* end::Menu item */}

        {/* begin::Menu item */}
        <div className='menu-item px-3 my-0'>
          <a
            href='#'
            className={clsx('menu-link px-3 py-2', {active: menuMode === 'system'})}
            onClick={() => switchMode('system')}
          >
            <span className='menu-icon' data-kt-element='icon'>
              <KTSVG path='/media/icons/duotune/general/gen062.svg' className='svg-icon-3' />
            </span>
            <span className='menu-title'>System</span>
          </a>
        </div>
        {/* end::Menu item */}

        <div className='separator my-2'></div>

        {/* The "themes" array from useThemeCustomizer already includes built-in
            themes merged with Firestore-backed custom themes. We split them
            here so the dropdown shows two labelled groups. */}
        {(() => {
          const builtIns = themes.filter((t) => (t as any).isBuiltIn)
          const customs = themes.filter((t) => !(t as any).isBuiltIn)
          return (
            <>
              {/* Built-in themes — terminal + any future shipped-with-app themes */}
              <div className='menu-item px-3'>
                <div className='menu-content px-3 pb-1'>
                  <span className='text-muted text-uppercase fs-8 ls-1'>Built-in Themes</span>
                </div>
              </div>

              <div className='menu-item px-3 my-0'>
                <a
                  href='#'
                  className={clsx('menu-link px-3 py-2', {active: activeThemeId === null})}
                  onClick={(e) => {
                    e.preventDefault()
                    setActiveThemeId(null)
                  }}
                >
                  <span className='menu-icon' data-kt-element='icon'>
                    <KTSVG path='/media/icons/duotune/general/gen027.svg' className='svg-icon-3' />
                  </span>
                  <span className='menu-title'>Default</span>
                </a>
              </div>

              {builtIns.map((t) => (
                <div className='menu-item px-3 my-0' key={t.id}>
                  <a
                    href='#'
                    className={clsx('menu-link px-3 py-2', {active: activeThemeId === t.id})}
                    onClick={(e) => {
                      e.preventDefault()
                      setActiveThemeId(t.id)
                    }}
                    title={t.description}
                  >
                    <span className='menu-icon' data-kt-element='icon'>
                      <KTSVG path='/media/icons/duotune/abstract/abs015.svg' className='svg-icon-3' />
                    </span>
                    <span className='menu-title'>{t.name}</span>
                  </a>
                </div>
              ))}

              {/* Custom themes (global Firestore-backed overrides) */}
              <div className='separator my-2'></div>
              <div className='menu-item px-3'>
                <div className='menu-content px-3 pb-1'>
                  <span className='text-muted text-uppercase fs-8 ls-1'>Custom Themes</span>
                </div>
              </div>
              {customs.length === 0 ? (
                <div className='menu-item px-3'>
                  <div className='menu-content px-3 py-2 text-muted fs-7'>No custom themes</div>
                </div>
              ) : (
                customs.map((t) => (
                  <div className='menu-item px-3 my-0' key={t.id}>
                    <a
                      href='#'
                      className={clsx('menu-link px-3 py-2', {active: activeThemeId === t.id})}
                      onClick={(e) => {
                        e.preventDefault()
                        setActiveThemeId(t.id)
                      }}
                    >
                      <span className='menu-icon' data-kt-element='icon'>
                        <KTSVG path='/media/icons/duotune/general/gen056.svg' className='svg-icon-3' />
                      </span>
                      <span className='menu-title'>{t.name}</span>
                    </a>
                  </div>
                ))
              )}
            </>
          )
        })()}

        <div className='separator my-2'></div>
        <div className='menu-item px-3 my-0'>
          <a href='/user/account/theme-customizer' className='menu-link px-3 py-2'>
            <span className='menu-icon' data-kt-element='icon'>
              <KTSVG path='/media/icons/duotune/general/gen019.svg' className='svg-icon-3' />
            </span>
            <span className='menu-title'>Open Theme Customizer</span>
          </a>
        </div>
      </div>
      {/* end::Menu */}
    </>
  )
}

export {ThemeModeSwitcher}
